import { DateTimeFormats, filterNotEmpty } from '@beeline.com/bp-design-system';
import { MappedNotification } from 'common/header/notifications/Notifications.types';
import { getMetaData, getNotificationState } from 'common/header/notifications/hooks/Notification.utils';
import { DateTimeFormatterFn } from '@utmost.co/utmost-design-system';
import { useFormatNotificationMessage } from 'src/components/common/header/translations/useNotificationsIntl';
import { capitalizeOnlyFirstLetter } from 'utils/textUtil';

export const getImportTitle = (
  notification: Partial<MappedNotification>,
  format: ReturnType<typeof useFormatNotificationMessage>,
  dateTimeFormatter: DateTimeFormatterFn
) => ({
  title: getTitle(notification, format),
  description: getDescription(notification, format, dateTimeFormatter)
});

const getTitle = (
  notificationViewModel: Partial<MappedNotification>,
  format: ReturnType<typeof useFormatNotificationMessage>
): string => {
  const state = getNotificationState(notificationViewModel.status);
  const resourceName = importResourceTitle(notificationViewModel) ?? '';
  if (state.isReady) {
    return format('importTitleSuccess', { resourceName });
  }
  if (state.isError) {
    return format('importTitleError', { resourceName });
  }
  if (state.isQueued) {
    return format('importTitleQueued', { resourceName });
  }
  return state.inProgress ? format('importTitleInProgress', { resourceName }) : '';
};

const getDescription = (
  notification: Partial<MappedNotification>,
  format: ReturnType<typeof useFormatNotificationMessage>,
  dateTimeFormatter: DateTimeFormatterFn
): string => {
  const state = getNotificationState(notification.status);
  if (state.isReady || state.isError) {
    return getImportUCICompletedDescription(notification, format, dateTimeFormatter);
  }
  if (state.isQueued) {
    return getImportUCIQueuedDescription(format);
  }
  return state.inProgress ? getImportUCIInProgressDescription(notification, format, dateTimeFormatter) : '';
};

const importResourceTitle = (notification: Partial<MappedNotification>) => capitalize(importResourceName(notification));

const capitalize = (str: string | undefined) => {
  if (!str) {
    return str;
  }
  return str.split('_').map(capitalizeOnlyFirstLetter).join(' ');
};

const importResourceName = (notificationViewModel: Partial<MappedNotification>) =>
  getImportEntityType(getMetaData(notificationViewModel)) ?? undefined;

const getImportUCICompletedDescription = (
  notificationViewModel: Partial<MappedNotification>,
  format: ReturnType<typeof useFormatNotificationMessage>,
  dateTimeFormatter: DateTimeFormatterFn
): string => {
  const metadata = getMetaData(notificationViewModel);
  return format('notifications.importsUCICompleted', {
    totalRecords: metadata?.find((e) => e.key === 'PROCESSED_RECORD_COUNT')?.value,
    recordsWithError: metadata?.find((e) => e.key === 'FAILED_RECORD_COUNT')?.value,
    type: getImportEntityType(filterNotEmpty(metadata)),
    startDate: getImporterDate(metadata, 'IMPORTER_STARTED_AT', dateTimeFormatter),
    endDate: getImporterDate(metadata, 'IMPORTER_FINISHED_AT', dateTimeFormatter)
  });
};

export const getImportEntityType = (
  metadata: { key?: string | null | undefined; value?: string | null | undefined }[] | undefined | null
) => {
  const result = metadata?.find((e) => e.key === 'IMPORTER_ENTITY_TYPE')?.value;
  return result === 'CUSTOM_OBJECTS' ? 'FINANCIAL_FIELD_VALUES' : result;
};

const getImportUCIInProgressDescription = (
  notificationViewModel: Partial<MappedNotification>,
  format: ReturnType<typeof useFormatNotificationMessage>,
  dateTimeFormatter: DateTimeFormatterFn
): string => {
  const metadata = getMetaData(notificationViewModel);

  return format('notifications.importsUCIInProgress', {
    type: metadata?.find((e) => e?.key === 'IMPORTER_ENTITY_TYPE')?.value,
    startDate: dateTimeFormatter(metadata?.find((e) => e?.key === 'IMPORTER_STARTED_AT')?.value, {
      utc: true,
      format: DateTimeFormats.WEEKDAY_LONG_DAY_MONTH
    })
  });
};

const getImportUCIQueuedDescription = (format: ReturnType<typeof useFormatNotificationMessage>): string =>
  format('notifications.importsUCIQueued');

const getImporterDate = (
  metadata: Partial<MappedNotification>['metadata'],
  key: string,
  dateTimeFormatter: DateTimeFormatterFn
) => {
  const value = metadata?.find((e) => e?.key === key)?.value;
  if (value?.length && value !== 'null')
    return dateTimeFormatter(value, {
      utc: true,
      format: DateTimeFormats.WEEKDAY_LONG_DAY_MONTH
    });
  return ' - ';
};
